import { Stack } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl';

import AddRecordButton from '@components/AddRecordButton';
import ContentLoader from '@components/ContentLoader';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import ViewTab from '@components/ViewTab';

import { ADD_IMMUNIZATION } from '@constants/routes';

import { useSession } from '@hooks/useSession';
import { useViewable } from '@hooks/useViewable';

import { NoRecordsWrapper } from '@layouts/Layouts/BigTitleLayout';

import type { ImmunizationRecord } from '@models/immunization';

import { capitalize } from '@utils/format';

import ImmunizationRow from './ImmunizationRow';

const messages = defineMessages({
  title: {
    id: 'immunizationsPage.title',
    defaultMessage: 'Immunizations',
  },
  previousImmunizations: {
    id: 'immunizationsPage.previousImmunizations',
    defaultMessage: 'Previous Immunizations',
  },
  pastDueImmunizations: {
    id: 'immunizationsPage.pastDueImmunizations',
    defaultMessage: 'Past Due Immunizations',
  },
  currentImmunizations: {
    id: 'immunizationsPage.currentImmunizations',
    defaultMessage: 'Current Immunizations',
  },
  standard: {
    id: 'immunizationsPage.standard',
    defaultMessage: 'standard',
  },
  travel: {
    id: 'immunizationsPage.travel',
    defaultMessage: 'travel',
  },
  noStandard: {
    id: 'immunizationsPage.noStandard',
    defaultMessage: 'No Standard Immunizations',
  },
  noTravel: {
    id: 'immunizationsPage.noTravel',
    defaultMessage: 'No Travel Immunizations',
  },
  addRecord: {
    id: 'immunizationsPage.addRecord',
    defaultMessage: 'Add an Immunization',
  },
  noRecordsFound: {
    id: 'immunizationsPage.noRecordsFound',
    defaultMessage: 'No Known {type} Immunization Records',
  },
});

interface ImmunizationListProps {
  immunizations: ImmunizationRecord[];
  loading: boolean;
}

const ImmunizationsList: React.FC<ImmunizationListProps> = ({
  immunizations,
  loading,
}) => {
  const { formatMessage } = useIntl();
  const {
    state: { isClient },
  } = useSession();

  const tabs = [
    { id: 'standard', value: formatMessage(messages.standard) },
    { id: 'travel', value: formatMessage(messages.travel) },
  ];
  const [activeTab, setActiveTab] = useState(tabs[0]);

  const { past, present } = immunizations
    .filter((imm) =>
      activeTab.id === 'standard' ? imm.isStandard() : imm.isTravel()
    )
    .sort((a, b) => {
      let dateA: dayjs.Dayjs | null = a.getExpiryDate()
        ? dayjs(a.getExpiryDate())
        : null;
      let dateB: dayjs.Dayjs | null = b.getExpiryDate()
        ? dayjs(b.getExpiryDate())
        : null;
      if (!dateA && !dateB) {
        dateA = a.getImmunizationDate() ? dayjs(a.getImmunizationDate()) : null;
        dateB = b.getImmunizationDate() ? dayjs(b.getImmunizationDate()) : null;
      }
      if (!dateA && dateB) {
        return -1;
      } else if (dateA && !dateB) {
        return 1;
      } else if (dateA && dateB) {
        return dateA.isAfter(dateB) ? -1 : 1;
      }
      return 0;
    })
    .reduce<{ present: ImmunizationRecord[]; past: ImmunizationRecord[] }>(
      (acc, curr) => {
        const existing = acc.present.some((i) => {
          return i.vaccineName === curr.vaccineName;
        });
        if (existing) {
          acc.past.push(curr);
        } else {
          acc.present.push(curr);
        }
        return acc;
      },
      { present: [], past: [] }
    );

  const current = present.filter((imm) => {
    const date = imm.getExpiryDate();
    if (!date) {
      return true;
    }
    return dayjs(date).isAfter(new Date());
  });

  const pastDue = present.filter((imm) => {
    const date = imm.getExpiryDate();
    if (!date) {
      return false;
    }
    return dayjs(date).isBefore(new Date());
  });

  useViewable([...present, ...past]);

  return (
    <ContentLoader loading={loading}>
      <Stack
        maxWidth="500px"
        alignItems="center"
        justifyContent="center"
        spacing={1}
        sx={{ pt: 2 }}
      >
        <ViewTab
          changeTab={setActiveTab}
          selected={activeTab}
          tabArray={tabs}
        />
        <ImmunizationRow
          immunizations={pastDue}
          title={formatMessage(messages.pastDueImmunizations)}
        />
        <ImmunizationRow
          immunizations={current}
          title={formatMessage(messages.currentImmunizations)}
        />
        <ImmunizationRow
          hideExpiry
          immunizations={past}
          title={formatMessage(messages.previousImmunizations)}
        />

        {pastDue.length + current.length + past.length === 0 && (
          <NoRecordsWrapper>
            {formatMessage(messages.noRecordsFound, {
              type: capitalize(activeTab.value),
            })}
          </NoRecordsWrapper>
        )}
        {isClient && (
          <AddRecordButton link={ADD_IMMUNIZATION}>
            {formatMessage(messages.addRecord)}
          </AddRecordButton>
        )}
      </Stack>
    </ContentLoader>
  );
};

export default withErrorBoundary(ImmunizationsList, {
  FallbackComponent: ErrorBoundaryFallback,
});
